import type {
  FocusEvent,
  FocusEventHandler,
  MouseEvent,
  MouseEventHandler,
  ReactElement,
} from 'react';
import { cloneElement, useState } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/core/tooltip';

type TriggerHandlers = {
  onFocus?: FocusEventHandler<HTMLElement>;
  onBlur?: FocusEventHandler<HTMLElement>;
  onMouseEnter?: MouseEventHandler<HTMLElement>;
  onMouseLeave?: MouseEventHandler<HTMLElement>;
};

type CellValidationTooltipProps = {
  message: string | null;
  children: ReactElement;
};

export function CellValidationTooltip({ message, children }: CellValidationTooltipProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [previousMessage, setPreviousMessage] = useState(message);

  if (message !== previousMessage) {
    setPreviousMessage(message);

    if (!message) {
      setIsFocused(false);
      setIsHovered(false);
    }
  }

  const childProps = children.props as TriggerHandlers;
  const trigger = cloneElement(children, {
    onFocus: (event: FocusEvent<HTMLElement>) => {
      childProps.onFocus?.(event);
      setIsFocused(true);
    },
    onBlur: (event: FocusEvent<HTMLElement>) => {
      childProps.onBlur?.(event);
      setIsFocused(false);
    },
    onMouseEnter: (event: MouseEvent<HTMLElement>) => {
      childProps.onMouseEnter?.(event);
      setIsHovered(true);
    },
    onMouseLeave: (event: MouseEvent<HTMLElement>) => {
      childProps.onMouseLeave?.(event);
      setIsHovered(false);
    },
  });

  if (!message) {
    return trigger;
  }

  const open = isFocused || isHovered;

  return (
    <Tooltip open={open}>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      {open ? <TooltipContent sideOffset={4}>{message}</TooltipContent> : null}
    </Tooltip>
  );
}
